/**
 * Settings route — a segmented control over four tabs (Plan / Display /
 * Account / About) rather than one long scroll. The page is an
 * add-features-here surface that had ballooned into a 6-card stack; the
 * tabs group related concerns and keep mobile navigation shallow.
 *
 * Tabs are LOCAL STATE (not sub-routes) — Settings sub-sections don't
 * need deep-linking, and keeping the single `/settings` route avoids
 * breaking the many `navigate("/settings")` callers. The visual is the
 * same segmented control as `features/skills/components/SkillsTabs`.
 *
 * Display preferences are localStorage-backed via `lib/preferences.ts`.
 * Subscription state is server-side (`lib/billing.ts:getBillingStatus`).
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  createPortalSession,
  getBillingStatus,
  PLAN_LABELS,
  type BillingStatus,
} from "@/features/billing/lib/billing";
import { UsageCard } from "@/features/billing/components/UsageCard";
import { NavIcons } from "@/shared/components/icons";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { useAuth } from "@/features/auth/lib/authContext";
import { ResetSkillProfile } from "@/features/skills/components/ResetSkillProfile";
import { useSpotLayout, type SpotLayout } from "@/shared/preferences";

type SettingsTab = "plan" | "display" | "account" | "about";

const SETTINGS_TABS: { id: SettingsTab; label: string }[] = [
  { id: "plan", label: "Plan" },
  { id: "display", label: "Display" },
  { id: "account", label: "Account" },
  { id: "about", label: "About" },
];

export function Settings() {
  const { status, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const userId = status.kind === "signedIn" ? status.userId : "?";
  const [spotLayout, setSpotLayout] = useSpotLayout();
  const [tab, setTab] = useState<SettingsTab>("plan");

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 space-y-6">
      <PageHeader Icon={NavIcons.settings} title="Settings" />

      <SettingsTabs active={tab} onChange={setTab} />

      {tab === "plan" && <SubscriptionSection />}

      {tab === "display" && (
        <section className="rounded-xl border border-border bg-surface p-5">
          <h2 className="text-sm uppercase tracking-wider text-text-muted mb-3">
            Display
          </h2>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-text font-medium">Spot layout</div>
              <p className="text-xs text-text-muted mt-1">
                How drilled spots are visualized in chat. The simplified view is
                opt-in and meant for users comfortable reading structured
                transcripts.
              </p>
            </div>
            <SpotLayoutToggle value={spotLayout} onChange={setSpotLayout} />
          </div>
        </section>
      )}

      {tab === "account" && (
        <div className="space-y-6">
          <section className="rounded-xl border border-border bg-surface p-5">
            <h2 className="text-sm uppercase tracking-wider text-text-muted mb-3">
              Account
            </h2>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs text-text-muted">User ID</div>
                <div className="text-xs font-mono text-text mt-0.5">
                  {userId.slice(0, 8)}…
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                setSigningOut(true);
                try {
                  await signOut();
                } finally {
                  setSigningOut(false);
                }
              }}
              disabled={signingOut}
              className="mt-4 w-full px-4 py-3 rounded-lg border border-danger/50 text-danger text-sm font-medium hover:bg-danger/10 transition disabled:opacity-50"
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </section>

          <ResetSkillProfile />
        </div>
      )}

      {tab === "about" && (
        <div className="space-y-6">
          <section className="rounded-xl border border-border bg-surface p-5">
            <h2 className="text-sm uppercase tracking-wider text-text-muted mb-3">
              Feedback
            </h2>
            <p className="text-sm text-text-muted mb-4">
              Found a bug or have a suggestion? Send it straight to the team.
            </p>
            <Link
              to="/feedback"
              className="block w-full px-4 py-3 rounded-lg border border-border text-text text-sm font-medium text-center hover:border-accent/60 transition"
            >
              Send feedback
            </Link>
          </section>

          <section className="rounded-xl border border-border bg-surface p-5">
            <h2 className="text-sm uppercase tracking-wider text-text-muted mb-3">
              About
            </h2>
            <p className="text-sm text-text-muted">
              Holdem Drills: web. Built by Schlienger LLC.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}

/** Segmented control over the Settings tabs. Mirrors `SkillsTabs`'s look
 *  but is button + local-state driven (no routing). */
function SettingsTabs({
  active,
  onChange,
}: {
  active: SettingsTab;
  onChange: (tab: SettingsTab) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Settings sections"
      className="flex gap-1 rounded-xl border border-border bg-surface p-1"
    >
      {SETTINGS_TABS.map((t) => {
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            className={[
              "flex-1 text-center text-sm font-medium rounded-lg py-2 transition",
              isActive
                ? "bg-accent/15 text-accent"
                : "text-text-muted hover:text-text hover:bg-text/5",
            ].join(" ")}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

interface SpotLayoutToggleProps {
  value: SpotLayout;
  onChange: (value: SpotLayout) => void;
}

// ---------------------------------------------------------------------------
// Subscription section — tier badge + manage-subscription / upgrade CTA.
//
// Reads /billing/status on mount. The "Manage subscription" link only
// appears if the user has a Stripe Customer (i.e. they've been through
// Checkout at least once); otherwise it's "View pricing" → /pricing.
// ---------------------------------------------------------------------------

function SubscriptionSection() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getBillingStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function openPortal() {
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPortalLoading(false);
    }
  }

  // Three CTA shapes:
  //   - Has subscription history (active / past_due / canceled with
  //     stripe_customer_id) → "Manage subscription" → Stripe Portal
  //   - Free with no prior subscription → "Upgrade" → /pricing
  //   - billing_enabled=false on server → no CTA, just a chip explaining
  const isManageable =
    status?.subscriptionStatus === "active" ||
    status?.subscriptionStatus === "past_due";

  return (
    <section className="rounded-xl border border-border bg-surface p-5">
      <h2 className="text-sm uppercase tracking-wider text-text-muted mb-3">
        Subscription
      </h2>

      {error && (
        <div className="text-xs text-danger mb-3">{error}</div>
      )}

      {!status && !error && (
        <div className="text-sm text-text-muted">Loading…</div>
      )}

      {status && (
        <div className="space-y-3">
          <TierBadge status={status} />

          {/* Usage card — names each active cap, its allowance, status,
              and reset time (free shows daily + monthly). */}
          <UsageCard status={status} />

          {/* CTAs */}
          {!status.billingEnabled ? (
            <div className="text-xs text-text-muted/80 italic">
              Billing isn't enabled on this deployment yet.
            </div>
          ) : isManageable ? (
            <button
              type="button"
              onClick={openPortal}
              disabled={portalLoading}
              className="w-full px-4 py-3 rounded-lg border border-border text-text text-sm font-medium hover:border-accent/60 transition disabled:opacity-50"
            >
              {portalLoading ? "Opening Stripe…" : "Manage subscription"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              className="w-full px-4 py-3 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)]"
            >
              Upgrade
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function TierBadge({ status }: { status: BillingStatus }) {
  const label = tierLabel(status);
  const sub = tierSubtitle(status);
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="px-2 py-0.5 rounded-md bg-accent/15 border border-accent/30 text-accent text-[11px] font-semibold uppercase tracking-wider">
          {label}
        </span>
        {status.isFounder && (
          <span className="px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] font-semibold uppercase tracking-wider">
            Founder
          </span>
        )}
      </div>
      {sub && (
        <div className="text-xs text-text-muted mt-1.5">{sub}</div>
      )}
    </div>
  );
}

function tierLabel(s: BillingStatus): string {
  switch (s.subscriptionStatus) {
    case "free":
      return "Free";
    case "active": {
      const tierWord = s.planLevel ? PLAN_LABELS[s.planLevel] : "Paid";
      const periodWord = s.plan === "annual" ? "Annual" : "Monthly";
      return `${tierWord} · ${periodWord}`;
    }
    case "past_due":
      return "Past due";
    case "canceled":
      return "Canceled";
  }
}

function tierSubtitle(s: BillingStatus): string | null {
  if (
    (s.subscriptionStatus === "active" || s.subscriptionStatus === "past_due") &&
    s.currentPeriodEnd
  ) {
    const dt = new Date(s.currentPeriodEnd);
    return `Renews ${dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  }
  if (s.subscriptionStatus === "past_due") {
    return "Payment retry in progress. Update your card to keep access.";
  }
  return null;
}

function SpotLayoutToggle({ value, onChange }: SpotLayoutToggleProps) {
  const options: { id: SpotLayout; title: string; subtitle: string }[] = [
    {
      id: "table",
      title: "Table view",
      subtitle: "Poker felt with seats, chips, and dealer button. Default.",
    },
    {
      id: "simplified",
      title: "Simplified card",
      subtitle: "Structured transcript: pot, board, hand, and per-street history.",
    },
  ];
  return (
    <div role="radiogroup" aria-label="Spot layout" className="grid gap-2">
      {options.map((opt) => {
        const selected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.id)}
            className={[
              "text-left rounded-lg border px-3 py-2.5 transition",
              selected
                ? "border-accent bg-accent/10"
                : "border-border bg-bg/40 hover:border-accent/60",
            ].join(" ")}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-text">{opt.title}</div>
              {selected && (
                <span className="text-[10px] uppercase tracking-wider text-accent font-semibold">
                  Selected
                </span>
              )}
            </div>
            <div className="text-xs text-text-muted mt-0.5">{opt.subtitle}</div>
          </button>
        );
      })}
    </div>
  );
}
